import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { User, UserAccountMode, Agent } from '@moderepo/mode-internal-apis';
import { fetchFilteredUser, fetchFilteredUsers, ritzDataContext, selectLoggedInAgent, selectProjectById, selectProjectUsers }
    from '@moderepo/mode-data-state-ritz';
import { PaginationBar, UsersTableProps, UsersTable, UsersTableDataItem, useSearchParams, ITEMS_PER_PAGE_10, capitalizeAll, ITEMS_PER_PAGE_LIST,
    PAGING_MIN_PAGES, GenericMessageBox } from '@moderepo/mode-app-components';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useNavigate } from 'react-router-dom';
import { useRitzTranslation } from '../../utils/customHooks';
import { isUserPreviewCompOptions, SlideOutPanelCompType, UserPreviewCompOptions } from '../routes/defaultProjectConfig';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { RouteHelper } from '../routes/RouteHelper';



/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'true'
 */
interface UsersTableHiddenFields {
    readonly id?: boolean;
    readonly name?: boolean;
    readonly email?: boolean;
    readonly phoneNumber?: boolean;
    readonly verified?: boolean,
}



/**
 * Because this is a container, not a page, it doesn't and shouldn't have access to the route params. Therefore, the page
 * that display this container must provide these information.
 */
interface UsersListingContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly itemsPerPage?: number;
    readonly hiddenFields?: UsersTableHiddenFields | undefined;
}


/**
 * This is a container (SMART COMPONENT) used for displaying a list of user. Since this is a container, it will have
 * little to no UI. All the UI will be components combined together to build the content.
 */
export const UsersListingContainer: React.FC<UsersListingContainerProps> = (props: UsersListingContainerProps) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const { trans } = useRitzTranslation(project);
    const [selectedUserId, setSelectedUserId] = useState<number | undefined>();
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const userAccountMode = project ? project.userAccountMode : undefined;
    const navigate = useNavigate();

    // Get the custom UI config for this project. If this project has the flag 'enableCompanyView' set to true, we will hide some UI
    const projectCustomUIConfig = useMemo(() => {
        if (project) {
            return ProjectTypeHandlersManager.getInstance()
                .getProjectCustomUIHandler(project.supportToolSettings.projectType)?.getCustomUIConfig(loggedInAgent, project);
        }
        return undefined;
    }, [loggedInAgent, project]);



    // This useEffect is used for checking the slideOutPanelOptions to see we are showing User Preview
    // if we are, automatically set it as the selectedUserId
    useEffect(() => {
        if (isUserPreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data?.userId !== undefined) {
            setSelectedUserId(slideOutPanelOptions.compProps.data.userId);
        } else {
            setSelectedUserId(undefined);
        }
    }, [slideOutPanelOptions]);



    const {
        searchParams, onItemsPerPageChange, onSearchTextChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<{}>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {},
    });

    const users = selectProjectUsers(dataState, props.projectId, {
        searchText: searchParams.searchText,
        pageNumber: searchParams.currentPage,
        pageSize  : searchParams.itemsPerPage,
    });


    /**
     * This useEffect is used for fetching users on component init and on search params changed
     */
    useEffect(() => {
        const searchUser = async () => {

            const searchT = searchParams.searchText;
            const itemsPP = searchParams.itemsPerPage;
            const currentP = searchParams.currentPage;

            if (project) {
                // Search for user by name, email, phone number if project's userAccountMode is NOT BYOU
                // For BYOU project, we need to know the user's exact ID to lookup user.
                if (project.userAccountMode !== UserAccountMode.BYOU) {
                    await dataDispatch(fetchFilteredUsers(props.projectId, searchT, currentP, itemsPP));
                }
                const foundUsers = selectProjectUsers(dataState, props.projectId, searchT, currentP, itemsPP);

                if (foundUsers && foundUsers.items.length > 0) {
                    // We found users by the search text, use this result
                    // dataDispatch(setUsers(props.projectId, usersByNameEmailPhone));
                } else if (searchT && !Number.isNaN(Number(searchT))) {
                    // If can't find any user by email/phone AND if the search text is a number, try search by exact user id
                    await dataDispatch(fetchFilteredUser(props.projectId, Number(searchT), currentP, itemsPP));
                }
            }
        };

        if (!users) {
            searchUser();
        }
    }, [dataDispatch, users, searchParams.searchText, searchParams.itemsPerPage, searchParams.currentPage, props.projectId, project, dataState]);



    /**
     * Localize the user data before passing it to the userTableComp for display
     */
    const localizeUserData = useMemo((): readonly UsersTableDataItem[] | undefined => {
        if (users) {
            return users.items.map((user: User): UsersTableDataItem => {
                return {
                    actualValue : user,
                    displayValue: {
                        id         : user.id.toString(),
                        name       : user.name ? user.name : capitalizeAll(trans('label.none')),
                        email      : user.email ? user.email : capitalizeAll(trans('label.none')),
                        phoneNumber: user.phoneNumber ? user.phoneNumber : capitalizeAll(trans('label.none')),
                        verified   : capitalizeAll(trans(user.verified ? 'label.verified' : 'label.not_verified')),
                    },
                    selected: user.id === selectedUserId,
                };
            });
        }

        return [];
    }, [trans, users, selectedUserId]);


    /**
     * On preview user button clicked, set the slideOutPanelOptions to show the selected user
     * If 'userId' object is NOT specified, it will be treated as Creating New user
     */
    const onPreviewUserClicked = useCallback((userId: number | undefined) => {
        setSelectedUserId(userId);

        const options: UserPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.USER_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,
                    userId,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, uiDispatch]);



    /**
     * On user selected, take the user to the details page
     */
    const onUserSelected = useCallback((user: User) => {
        RouteHelper.getInstance(navigate).gotoUserPage({ projectId: props.projectId, userId: user.id });
    }, [navigate, props.projectId]);


    /**
     * On createUserClicked, we will unselect the current selected user if there is one selected and we will pass 'true' for createUser flag.
     */
    const onCreateUserClicked = useCallback(() => {
        onPreviewUserClicked(undefined);
    }, [onPreviewUserClicked]);


    /**
     * Create pagination settings and cache it
     */
    const paginationComp = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : users && users.range ? users.range.total : undefined,
                hasMoreItems    : users !== undefined && (users.range
                    ? users.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (users.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('terms.user', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, users, searchParams, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);


    // Create the table settings and cache it
    const tableFieldsSettings = useMemo((): UsersTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label: '',      // The column doesn't need a label so we can leave it blank
                },
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    hidden      : props.hiddenFields?.id,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.user_name')),
                    hidden      : props.hiddenFields?.name,
                },
                email: {
                    dataItemProp: 'email',
                    label       : capitalizeAll(trans('label.user_email')),
                    hidden      : props.hiddenFields?.email || userAccountMode !== UserAccountMode.EMAIL || projectCustomUIConfig?.enableCompanyView,
                },
                phoneNumber: {
                    dataItemProp: 'phoneNumber',
                    label       : capitalizeAll(trans('label.user_phone_number')),
                    hidden      : props.hiddenFields?.phoneNumber || userAccountMode !== UserAccountMode.PHONE
                        || projectCustomUIConfig?.enableCompanyView,
                },
                verified: {
                    dataItemProp: 'verified',
                    label       : capitalizeAll(trans('label.status')),
                    hidden      : props.hiddenFields?.verified,
                },
            },
        };
    }, [trans, props.hiddenFields, userAccountMode, projectCustomUIConfig]);


    return (
        <>
            {project && (
                <UsersTable
                    className={clsx(props.className)}
                    listData={localizeUserData}
                    title={capitalizeAll(trans('terms.user', { count: 2 }))}
                    icon={trans('icons.user')}
                    onSearchTextChanged={onSearchTextChange}
                    onListItemSelected={onUserSelected}
                    onPreviewItemClicked={(user: User) => {
                        onPreviewUserClicked(user.id);
                    }}
                    paginationComp={paginationComp}
                    texts={{
                        searchBox: {
                            placeholder: capitalizeAll(trans('label.search')),
                        },
                    }}
                    customPrimaryActions={project && project.userAccountMode !== UserAccountMode.BYOU ? [{
                        label  : capitalizeAll(trans('label.add')),
                        icon   : 'add',
                        variant: 'contained',
                        color  : 'primary',
                        onClick: onCreateUserClicked,
                    }] : []}
                    fieldsSettings={tableFieldsSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('terms.user', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('terms.user', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}

            {!project && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('UsersListingContainer', UsersListingContainer);
